import { useCallback, useRef, useState } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import { useQuery, UseQueryResult } from 'react-query';
import { InvoiceRetrieve, ErrorMessage } from 'types';
import { client } from 'utils/api-client';

const TERMINAL_DONE_STATUSES = new Set(['PAID', 'PAYMENT FAILED', 'VOID']);
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 mins

type UsePollInvoiceResult = UseQueryResult<AxiosResponse<InvoiceRetrieve>, AxiosError<ErrorMessage>> & {
  isTimedOut: boolean;
  resetPoll: () => void;
};

export default function usePollInvoice({
  invoiceId,
  isChargeNow,
}: {
  invoiceId: string;
  isChargeNow: boolean;
}): UsePollInvoiceResult {
  const startTimeRef = useRef<number | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);

  const queryFn = (): Promise<AxiosResponse<InvoiceRetrieve>> => {
    return client.get(`invoices/${invoiceId}/`);
  };

  const query = useQuery(['invoices', invoiceId, 'poll'], queryFn, {
    enabled: !!invoiceId,
    refetchIntervalInBackground: true,
    refetchInterval: data => {
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now();
      }
      if (Date.now() - startTimeRef.current > MAX_POLL_DURATION_MS) {
        if (!isTimedOut) setIsTimedOut(true);
        return false;
      }

      if (!data) return 2000;

      if (isChargeNow) {
        return TERMINAL_DONE_STATUSES.has(data.data.status) ? false : 2000;
      }

      // SetupIntent flow: done when terminalSetupIntentId is cleared or invoice is VOID
      return data.data.terminalSetupIntentId === null || data.data.status === 'VOID' ? false : 2000;
    },
  });

  // Restart the 5-min poll window from scratch. The refetch triggers
  // refetchInterval to re-evaluate with a fresh startTimeRef, resuming polling.
  const resetPoll = useCallback(() => {
    startTimeRef.current = null;
    setIsTimedOut(false);
    query.refetch();
  }, [query]);

  return { ...query, isTimedOut, resetPoll } as UsePollInvoiceResult;
}
